import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { useAuth } from "@via/hooks/useAuth.tsx";
import {
  useIntegrationTokens,
  useSelectIntegrationToken,
} from "@via/api/integration-tokens.api.tsx";
import { useAvailableAIModels } from "@via/pages/projects/runs/runs.api.tsx";
import { USER_ROLES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { requiredValidation } from "@via/utils/form-validators.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import {
  useCreateAssistant,
  useUploadKnowledgeSource,
} from "@via/pages/cortex/knowledge-builder/knowledge-builder.api.tsx";
import { useDebounceCallback } from "usehooks-ts";
import {
  getCortexAllowedFileExtensionsWithDot,
  isCortexAllowedFileExtension,
  Permissions,
  PermissionType,
  ServiceTokenListItem,
} from "common";
import { Spinner } from "@via/components/loading/loading";

interface AddAssistantDrawerProps {
  setOpen: (open: boolean) => void;
}

interface AddAssistantFormValues {
  name: string;
  foundationModel: string;
  promptsAndInstructions: string;
}

interface TokenSelectOption extends SelectOption {
  value: string;
  label: string;
}

const AI_TOKEN_TYPES = ["openai", "anthropic", "gemini"];
const INTEGRATION_TOKEN_PAGE_SIZE = 10;

const defaultInstructions = `You are a helpful research assistant specializing in bioinformatics. Your role is to help users analyze genomic data, explain complex biological concepts, and provide insights from scientific literature.`;

const ALLOWED_EXTENSIONS = getCortexAllowedFileExtensionsWithDot();
const MAX_FILES = 20;
const MAX_FILE_SIZE_MB = 20;

function isAllowedFile(file: File): boolean {
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  return isCortexAllowedFileExtension(ext);
}

export default function AddAssistantDrawer({
  setOpen,
}: AddAssistantDrawerProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [knowledgeSourceFiles, setKnowledgeSourceFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<number | null>(null);
  const [selectedTokenOption, setSelectedTokenOption] =
    useState<TokenSelectOption | null>(null);
  const [isSelectingToken, setIsSelectingToken] = useState(false);
  const [integrationTokenPage, setIntegrationTokenPage] = useState(0);
  const [searchInputForIntegrationToken, setSearchInputForIntegrationToken] =
    useState("");
  const [cumulativeIntegrationTokens, setCumulativeIntegrationTokens] =
    useState<ServiceTokenListItem[]>([]);

  const { user } = useAuth();

  const { mutateAsync: createAssistantAsync, isPending: isCreateLoading } =
    useCreateAssistant();
  const { mutateAsync: uploadSourceAsync } = useUploadKnowledgeSource();
  const { mutateAsync: selectIntegrationTokenAsync } =
    useSelectIntegrationToken();

  const { data: tokenDataList, isLoading: isTokenDataListLoading } =
    useIntegrationTokens(
      integrationTokenPage,
      INTEGRATION_TOKEN_PAGE_SIZE,
      [],
      [
        { id: "tokenTypeIn", value: AI_TOKEN_TYPES },
        { id: "name", value: searchInputForIntegrationToken },
      ],
      true
    );

  const fetchMoreIntegrationTokens = useCallback(() => {
    setIntegrationTokenPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage =
        Math.ceil((tokenDataList?.total ?? 0) / INTEGRATION_TOKEN_PAGE_SIZE) -
        1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [tokenDataList?.total]);

  useEffect(() => {
    if (!tokenDataList) return;

    setCumulativeIntegrationTokens(prevIntegrationTokens => {
      const newData = tokenDataList.data ?? [];
      if (!newData.length)
        return integrationTokenPage === 0 ? [] : prevIntegrationTokens;

      if (integrationTokenPage === 0) return newData;

      const existingIds = new Set(prevIntegrationTokens.map(p => p.id));
      const newTokens = newData.filter(p => !existingIds.has(p.id));
      return [...prevIntegrationTokens, ...newTokens];
    });
  }, [tokenDataList, integrationTokenPage, searchInputForIntegrationToken]);

  const debouncedHandleInputChangeForIntegrationToken = useDebounceCallback(
    (value: string) => {
      if (value === searchInputForIntegrationToken) return;
      setIntegrationTokenPage(0);
      setSearchInputForIntegrationToken(value);
    },
    300
  );

  const tokenOptions: TokenSelectOption[] = useMemo(() => {
    const options: TokenSelectOption[] = cumulativeIntegrationTokens.map(t => ({
      value: String(t.id),
      label: t.name,
    }));

    if (
      selectedTokenOption &&
      !options.some(o => o.value === selectedTokenOption.value)
    ) {
      options.unshift(selectedTokenOption as TokenSelectOption);
    }

    return options;
  }, [cumulativeIntegrationTokens, selectedTokenOption]);

  useEffect(() => {
    if (selectedTokenId != null) return;
    const first = cumulativeIntegrationTokens[0];
    if (!first) return;
    setSelectedTokenId(first.id);
    setSelectedTokenOption({
      value: String(first.id),
      label: first.name,
    });
  }, [selectedTokenId, cumulativeIntegrationTokens]);

  const { data: modelsData, isLoading: isModelsLoading } = useAvailableAIModels(
    selectedTokenId ?? undefined
  );

  const modelOptions: SelectOption[] = useMemo(
    () =>
      (modelsData?.models ?? []).map(m => ({
        label: m,
        value: m,
      })),
    [modelsData?.models]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<AddAssistantFormValues>({
    mode: "onChange",
    defaultValues: {
      name: "",
      foundationModel: "",
      promptsAndInstructions: defaultInstructions,
    },
  });

  useEffect(() => {
    if (modelOptions.length > 0) {
      const firstModel = modelOptions[0];
      if (firstModel && typeof firstModel.value === "string") {
        setValue("foundationModel", firstModel.value);
      }
    }
  }, [modelOptions, setValue]);

  useEffect(() => {
    setValue("foundationModel", "");
  }, [selectedTokenId, setValue]);

  const isAdmin = user?.userRole === USER_ROLES.ADMIN;

  const [permissions, setPermissions] = useState<PermissionType>(() =>
    isAdmin ? PermissionType.Public : PermissionType.UserOwned
  );

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedWriteUserGroups, setSelectedWriteUserGroups] = useState<
    number[]
  >([]);

  useEffect(() => {
    if (!isAdmin && permissions === PermissionType.Public) {
      setPermissions(PermissionType.UserOwned);
    }
  }, [isAdmin, permissions]);

  const isBusy =
    isSubmitting ||
    isCreateLoading ||
    isSelectingToken ||
    isTokenDataListLoading;

  const handleCancel = (): void => {
    if (isBusy) return;
    reset();
    setKnowledgeSourceFiles([]);
    setOpen(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files ?? []);
    const allowed = files.filter(isAllowedFile);
    const sizeOk = allowed.filter(
      f => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024
    );
    const remaining = Math.max(0, MAX_FILES - knowledgeSourceFiles.length);
    const toAdd = sizeOk.slice(0, remaining);
    setKnowledgeSourceFiles(prev => [...prev, ...toAdd]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = (index: number): void => {
    if (isBusy) return;
    setKnowledgeSourceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleTokenChange = async (
    option: SelectOption | null
  ): Promise<void> => {
    const nextOption: TokenSelectOption | null =
      option?.value != null && String(option.value).trim().length > 0
        ? {
            value: String(option.value),
            label: String(option.label ?? option.value),
          }
        : null;
    const nextId = nextOption ? Number(nextOption.value) : null;
    if (!Number.isFinite(nextId)) return;
    if (nextId === selectedTokenId) return;

    const previousId = selectedTokenId;
    const previousOption = selectedTokenOption;
    setSelectedTokenId(nextId);
    setSelectedTokenOption(nextOption);
    setIsSelectingToken(true);
    try {
      await selectIntegrationTokenAsync(String(nextId));
    } catch {
      // Notifications handled in api layer.
      setSelectedTokenId(previousId ?? null);
      setSelectedTokenOption(previousOption ?? null);
    } finally {
      setIsSelectingToken(false);
    }
  };

  const handleCreateAssistant = async (
    data: AddAssistantFormValues
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      const perms =
        permissions === PermissionType.Public
          ? Permissions.Public
          : permissions === PermissionType.GroupShared
            ? Permissions.GroupShared
            : Permissions.UserOwned;

      const { id } = await createAssistantAsync({
        name: data.name,
        model: data.foundationModel,
        prompt: data.promptsAndInstructions.trim() || null,
        perms,
        groupId: perms === Permissions.GroupShared ? selectedGroupId : null,
        writeGroupIds: selectedWriteUserGroups,
      });

      const filesToUpload = [...knowledgeSourceFiles];

      // Start uploads in background (do not block the drawer).
      // This matches the expected UX: user can continue using the product immediately.
      for (const file of filesToUpload) {
        void uploadSourceAsync({ assistantId: id, file }).catch(
          () => undefined
        );
      }

      reset();
      setKnowledgeSourceFiles([]);
      setOpen(false);
    } catch {
      // Notifications handled in api layer; keep drawer open for fixes/retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleCreateAssistant)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Add Assistant (RAG)
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none disabled:opacity-50"
                disabled={isBusy}
                onClick={handleCancel}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Create a Knowledge Builder assistant and optionally upload up to{" "}
              {MAX_FILES} files.
            </p>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div>
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Integration Token <span className="text-red-400">*</span>
                </label>
                <p className="mb-2 text-sm text-gray-500">
                  Select the token to use for model listing and knowledge
                  ingestion
                </p>
                <SelectComponent
                  name="integrationTokenId"
                  options={tokenOptions}
                  value={selectedTokenOption}
                  onChange={val =>
                    void handleTokenChange(val as SelectOption | null)
                  }
                  onInputChange={debouncedHandleInputChangeForIntegrationToken}
                  onFetchMoreOptions={fetchMoreIntegrationTokens}
                  placeholder={
                    tokenOptions.length > 0
                      ? "Select integration token"
                      : "Create an integration token (OpenAI, Claude, or Gemini)"
                  }
                  enableClear={false}
                  readOnly={isBusy}
                  isLoading={isSelectingToken || isTokenDataListLoading}
                />
              </div>

              <FormInputComponent
                control={control}
                name="name"
                label="Assistant Name"
                showRequiredField={true}
                placeholder="e.g., Research Assistant, Data Analyst Helper"
                validations={requiredValidation("Assistant Name")}
                disabled={isBusy}
              />

              <div>
                <label className="mb-2 flex items-center text-sm font-medium leading-6 text-gray-900">
                  Foundation Model
                  <span className="text-red-400">&nbsp;*</span>
                </label>
                <p className="mb-2 text-sm text-gray-500">
                  The underlying AI model that powers the assistant
                </p>
                <SelectComponent
                  control={control}
                  name="foundationModel"
                  rules={requiredValidation("Foundation Model")}
                  options={modelOptions}
                  placeholder={
                    modelOptions.length > 0
                      ? "Select foundation model"
                      : "Select an integration token to see models"
                  }
                  enableClear={false}
                  readOnly={isBusy || isModelsLoading}
                  isLoading={isModelsLoading}
                />
                {errors.foundationModel && (
                  <span className="text-xs text-rose-500">
                    {errors.foundationModel.message || "This field is required"}
                  </span>
                )}
              </div>

              <div>
                <FormTextAreaComponent
                  control={control}
                  name="promptsAndInstructions"
                  label="Prompts & Instructions"
                  showRequiredField={true}
                  placeholder="Describe who the assistant is and what it does"
                  rows={6}
                  validations={requiredValidation("Prompts & Instructions")}
                  disabled={isBusy}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Describe who the assistant is and what it does
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Knowledge Sources
                </label>
                <p className="mb-2 text-sm text-gray-500">
                  Files the assistant can reference. Max {MAX_FILES} files,{" "}
                  {MAX_FILE_SIZE_MB}MB each. You can add more after creating.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_EXTENSIONS.join(",")}
                  multiple
                  disabled={isBusy}
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
                />
                {knowledgeSourceFiles.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {knowledgeSourceFiles.map((file, index) => (
                      <span
                        key={`${file.name}-${index}`}
                        className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1 text-sm text-gray-700">
                        {file.name}
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => handleRemoveFile(index)}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
                          <span className="sr-only">Remove file</span>
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <fieldset>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Set Permissions
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    Who can use this assistant and what data it can access
                  </p>

                  <div className="mt-3 space-y-4">
                    {/* Public access */}
                    {isAdmin && (
                      <div className="relative flex items-start">
                        <div className="absolute flex h-6 items-center">
                          <input
                            id="permission-public"
                            checked={permissions === PermissionType.Public}
                            disabled={isBusy}
                            onChange={() =>
                              setPermissions(PermissionType.Public)
                            }
                            value={PermissionType.Public}
                            aria-describedby="permission-public-description"
                            type="radio"
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                          />
                        </div>
                        <div
                          className={classNames(
                            isBusy ? "opacity-50" : "",
                            "pl-7 text-sm leading-6"
                          )}>
                          <label
                            htmlFor="permission-public"
                            className="font-medium text-gray-900">
                            Public access
                          </label>
                          <p
                            id="permission-public-description"
                            className="text-gray-500">
                            Everyone with the link will see this assistant.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Group access */}
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          id="permission-group"
                          checked={permissions === PermissionType.GroupShared}
                          disabled={isBusy}
                          onChange={() =>
                            setPermissions(PermissionType.GroupShared)
                          }
                          value={PermissionType.GroupShared}
                          aria-describedby="permission-group-description"
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div
                        className={classNames(
                          isBusy ? "opacity-50" : "",
                          "pl-7 text-sm leading-6"
                        )}>
                        <label
                          htmlFor="permission-group"
                          className="font-medium text-gray-900">
                          Private to group members
                        </label>
                        <p
                          id="permission-group-description"
                          className="text-gray-500">
                          Only members of this assistant would be able to
                          access.
                        </p>
                      </div>
                    </div>

                    {permissions === PermissionType.GroupShared && (
                      <div className="pl-7">
                        <UserGroupSelectComponent
                          name="groupId"
                          menuPlacement="top"
                          isDisabled={isBusy}
                          missingId={
                            selectedGroupId != null ? [selectedGroupId] : []
                          }
                          onChange={selected => {
                            setSelectedGroupId(
                              selected?.value != null
                                ? Number(selected.value)
                                : null
                            );
                          }}
                        />
                      </div>
                    )}

                    {/* Private access */}
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          id="permission-private"
                          checked={permissions === PermissionType.UserOwned}
                          disabled={isBusy}
                          value={PermissionType.UserOwned}
                          onChange={() =>
                            setPermissions(PermissionType.UserOwned)
                          }
                          aria-describedby="permission-private-description"
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div
                        className={classNames(
                          isBusy ? "opacity-50" : "",
                          "pl-7 text-sm leading-6"
                        )}>
                        <label
                          htmlFor="permission-private"
                          className="font-medium text-gray-900">
                          Private to you
                        </label>
                        <p
                          id="permission-private-description"
                          className="text-gray-500">
                          You are the only one able to access this assistant.
                        </p>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </div>

              <div>
                <hr className="my-4" />
                <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
                  Group Permission to Write
                </label>
                <div className="mt-2 space-y-4">
                  <UserGroupSelectComponent
                    name="writeGroupIds"
                    isDisabled={isBusy}
                    isMulti={true}
                    missingId={selectedWriteUserGroups}
                    onChange={selected => {
                      const selectedValues = Array.isArray(selected)
                        ? selected
                        : selected
                          ? [selected]
                          : [];
                      setSelectedWriteUserGroups(
                        selectedValues
                          .map(s => Number(s.value))
                          .filter(v => Number.isFinite(v))
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-end gap-3 px-4 py-4">
        <button
          type="button"
          disabled={isBusy}
          onClick={handleCancel}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isBusy}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          <Spinner loading={isSubmitting}>Create Assistant</Spinner>
        </button>
      </div>
    </form>
  );
}
